import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, ChevronRight } from 'lucide-react';
import ExpandedCard from '../AnimalHelpGuide/ExpandedCard/ExpandedCard';

const HelpGuideCard = ({ guide, onSelect }) => {
  const { t } = useTranslation();
  return (
    <li className="list-none">
      <button
        type="button"
        onClick={onSelect}
        className="w-full text-left bg-white rounded-xl border border-gray-200 overflow-hidden cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
        aria-label={`Open help guide: ${guide.title}`}
      >
        <div className="flex gap-3 p-4 items-start">
          <span
            className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center"
            aria-hidden="true"
          >
            <BookOpen className="w-5 h-5" />
          </span>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-gray-900 text-sm line-clamp-2">
                {guide.title}
              </h4>
              <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0 bg-teal-100 text-teal-700">
                {t('help_guide_label', 'Help Guide')}
              </span>
            </div>

            {guide.description ? (
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                {guide.description}
              </p>
            ) : null}
          </div>

          <ChevronRight
            className="flex-shrink-0 w-4 h-4 text-gray-400 mt-1"
            aria-hidden="true"
          />
        </div>
      </button>
    </li>
  );
};

const HelpGuideCards = ({ guides }) => {
  const { t } = useTranslation();
  const [selectedGuide, setSelectedGuide] = useState(null);

  const list = Array.isArray(guides) ? guides : [];
  if (list.length === 0) return null;

  return (
    <>
      <section aria-label={t('related_help_guides', 'Related help guides')}>
        <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">
          {t('from_our_help_guides', 'From Our Help Guides')}
        </h3>

        <ul className="space-y-3">
          {list.map((guide) => (
            <HelpGuideCard
              key={guide.id || guide.key}
              guide={guide}
              onSelect={() => setSelectedGuide(guide)}
            />
          ))}
        </ul>
      </section>

      <AnimatePresence>
        {selectedGuide && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100]"
          >
            <ExpandedCard
              topic={selectedGuide}
              onClose={() => setSelectedGuide(null)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HelpGuideCards;
